import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Search, AlertCircle, Shield } from 'lucide-react';
import { ruleCategories } from './laws';

const INITIAL_RULES_COUNT = 24;
const RULES_STEP = 24;

export default function Laws() {
  const [active, setActive] = useState(ruleCategories[0].id);
  const [query, setQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(INITIAL_RULES_COUNT);
  const deferredQuery = useDeferredValue(query);

  const activeCategory = useMemo(() => {
    return ruleCategories.find((category) => category.id === active) || ruleCategories[0];
  }, [active]);

  const filteredRules = useMemo(() => {
    const cleanQuery = deferredQuery.trim();
    if (!cleanQuery) return activeCategory.rules;
    return activeCategory.rules.filter((rule) => rule.includes(cleanQuery));
  }, [deferredQuery, activeCategory]);

  const visibleRules = useMemo(() => {
    return filteredRules.slice(0, visibleCount);
  }, [filteredRules, visibleCount]);

  useEffect(() => {
    setVisibleCount(INITIAL_RULES_COUNT);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [active, deferredQuery]);

  return (
    <section id="laws" className="pt-28 pb-20 min-h-screen relative overflow-hidden bg-[#050505]" dir="rtl">
      <div className="absolute top-0 right-0 w-[28rem] h-[28rem] bg-neon-cyan/5 rounded-full blur-[90px] pointer-events-none transform-gpu" />

      <div className="container mx-auto px-6 z-10 relative">
        <h2 className="text-4xl md:text-6xl font-black text-center mb-12 text-white tracking-wide">
          دليل <span className="text-neon-cyan">القوانين</span>
        </h2>

        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:w-1/4 flex flex-col gap-3 lg:sticky lg:top-28 self-start max-h-[72vh] overflow-y-auto pr-2">
            {ruleCategories.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => {
                  setActive(category.id);
                  setQuery('');
                }}
                className={`text-right px-5 py-4 rounded-xl font-bold transition-colors duration-200 flex items-center gap-4 ${
                  active === category.id
                    ? 'bg-neon-cyan/15 text-neon-cyan border-r-4 border-neon-cyan'
                    : 'bg-white/[0.03] border border-white/5 text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <span className="text-xl">{category.icon}</span>
                <span>{category.label}</span>
              </button>
            ))}
          </div>

          <div className="lg:w-3/4 bg-white/[0.025] p-6 md:p-10 rounded-3xl border border-white/10 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-neon-cyan to-transparent opacity-50" />

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
              <div className="flex items-center gap-3">
                <Shield className="text-neon-cyan shrink-0" size={36} />
                <h3 className="text-3xl md:text-4xl font-black text-white">{activeCategory.label}</h3>
              </div>

              <div className="relative w-full md:w-72">
                <input
                  type="text"
                  placeholder="ابحث في القوانين..."
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  className="w-full bg-black/50 border border-white/10 rounded-full px-6 py-4 pr-14 text-white focus:outline-none focus:border-neon-cyan transition-colors"
                />
                <Search className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400" size={22} />
              </div>
            </div>

            <p className="text-gray-300 mb-8 text-xl leading-relaxed">{activeCategory.summary}</p>

            {activeCategory.alert && (
              <div className="bg-red-500/10 border border-red-500/30 p-5 md:p-6 rounded-2xl flex items-start gap-4 mb-10">
                <AlertCircle className="text-red-500 shrink-0 mt-1" size={28} />
                <p className="text-red-200 leading-relaxed font-bold text-lg">{activeCategory.alert}</p>
              </div>
            )}

            <div className="space-y-4">
              {filteredRules.length > 0 ? (
                visibleRules.map((rule, index) => (
                  <div
                    key={`${activeCategory.id}-${index}`}
                    className="p-5 md:p-6 bg-white/[0.035] border border-white/10 rounded-2xl text-gray-200 flex gap-5 group hover:border-neon-cyan/40 hover:bg-neon-cyan/5 transition-colors duration-200"
                  >
                    <div className="w-10 h-10 shrink-0 rounded-full bg-neon-cyan/10 flex items-center justify-center border border-neon-cyan/30 group-hover:bg-neon-cyan group-hover:text-black transition-colors duration-200">
                      <span className="font-black text-neon-cyan group-hover:text-black">{index + 1}</span>
                    </div>
                    <p className="leading-relaxed text-lg pt-1">{rule}</p>
                  </div>
                ))
              ) : (
                <div className="text-center py-10 text-gray-500 font-bold">لا توجد قوانين مطابقة لبحثك.</div>
              )}
            </div>

            {visibleCount < filteredRules.length && (
              <div className="flex justify-center mt-8">
                <button
                  type="button"
                  onClick={() => setVisibleCount((count) => count + RULES_STEP)}
                  className="px-8 py-3 rounded-full bg-neon-cyan/10 text-neon-cyan border border-neon-cyan/40 hover:bg-neon-cyan hover:text-black font-bold transition-colors"
                >
                  عرض المزيد
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
